﻿import React, { useState, useEffect } from 'react';
import { ResidueOnEvaporationBenchSheet } from '../models/residue-on-evaporation-bench-sheet';
import { AssetType, BenchSheetTechnologyType, DocumentType, NoteType, Entities } from '../models/enums';
import BenchSheetHeaderComponent from './BenchSheetHeaderComponent';
import BenchSheetWeightData from './BenchSheetWeightData';
import ResidueOnIgnitionInstruments from './ResidueOnIgnitionInstruments';
import BenchSheetData from './BenchSheetData';
import NoteViewer from './NoteViewer';
import { CrudFunctions } from '../utilities/crud-functions';
import StandardsViewer from './StandardsViewer';
import TicketLogEntryForm from './TicketLogEntryForm';
import { UserDataStore } from '../store/userDataStore';
import BenchSheetToolbar from './BenchSheetToolbar';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { ItemBasicInformation } from '../models/item-basic-information';
import { DocumentationConstants } from '../store/documentation-constants';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import DmaFooter from './DmaFooter';

import { Popup as DialogPopup } from 'devextreme-react/popup';

let key = 1;

export default function ResidueOnEvaporationBenchSheetForm() {
    const [benchSheet, setBenchSheet] =
        useState<ResidueOnEvaporationBenchSheet>();

    const [canEdit, setCanEdit] =
        useState<boolean>(false);

    const [ticketLogEntryDialogVisible, setTicketLogEntryDialogVisible] =
        useState<boolean>(false);

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        setAuthorizations();
        getAllData();
    }, []);

    const parts = window.location.href.split('/');
    const benchSheetId = parts[parts.length - 1];
    const technologyId = BenchSheetTechnologyType.ResidueOnEvaporation;

    key++;

    return (
        <div className="pw-content">
            <div className="pw-container">
                <BenchSheetToolbar title="ROE Bench Sheet"
                    subtitle="Residue on Evaporation"
                    auditRowPointer={benchSheetId} entity={Entities.RoeBenchSheets}
                    technologyId={BenchSheetTechnologyType.ResidueOnEvaporation}
                    documentationId={DocumentationConstants.ROEBenchSheet}
                    showItemInformation={showBenchSheetInformationDialog}
                    showTicketLog={showTicketLogEntryDialog} />

                <BenchSheetHeaderComponent benchSheetId={benchSheetId} readOnly={!canEdit} />

                {benchSheet &&
                    <ResidueOnIgnitionInstruments benchSheet={benchSheet} readOnly={!canEdit} />
                }

                <div className="pw-margin-top-1">
                    <BenchSheetWeightData benchSheetId={benchSheetId}
                        technologyId={technologyId} isReadOnly={!canEdit}
                        datagridHeight="20rem" title="ROE Weight Data" />
                </div>

                <div className="pw-margin-top-1">
                    <NoteViewer height='10rem' title="Bench Sheet Note"
                        noteType={NoteType.Method} parentId={benchSheetId} />
                </div>

                <div className="pw-margin-top-1">
                    <BenchSheetData parentId={benchSheetId}
                        readOnly={!canEdit} canDelete={true}
                        technology={technologyId}
                        hideImageHandler={true}
                        documentType={DocumentType.General} />
                </div>

                {/*
                <div>
                    <StandardsViewer batchId={benchSheet && benchSheet.batchId ?
                        benchSheet.batchId : undefined}
                        technologyId={technologyId} />
                </div>
                */}

                <DmaFooter />
            </div>

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {ticketLogEntryDialogVisible &&
                <DialogPopup visible={ticketLogEntryDialogVisible}
                    onHiding={hideTicketLogEntryDialog}
                    title="Ticket Log Entry"
                    width={'55rem'}
                    height={'45rem'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <TicketLogEntryForm formName="ROE Bench Sheet" contextData={benchSheet}
                        onCancel={hideTicketLogEntryDialog}
                        onComplete={hideTicketLogEntryDialog}
                        key={`TLE${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function setAuthorizations() {
        const user = UserDataStore.getCurrentUser();

        const canEditRoles = ["SA", "MF-A", "MF-L", "MF-T", "PP-A", "PP-L", "PP-T", "DIST-A", "BA-A", "QCP-A", "QCD-A", "RD-A", "RD-L", "RD-T"];
        const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

        setCanEdit(canEdit);
    }

    async function getBenchSheet() {
        await CrudFunctions.getData(`benchsheets/benchsheet/${benchSheetId}`)
            .then(data => {
                setBenchSheet(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet");
            });
    }

    //show/hide

    function showTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(true);
    }

    function hideTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(false);
    }

    function showBenchSheetInformationDialog() {
        if (benchSheet) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: benchSheet.benchSheetId,
                createTimestamp: benchSheet.createTimestamp,
                lastModifiedTimestamp: benchSheet.modifyTimestamp,
                createUser: benchSheet.createUser,
                modifyUser: benchSheet.modifyUser
            };

            setItemBasicInformation(itemInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    async function getAllData() {
        await getBenchSheet();
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

}
